import {Avatar, Flag} from 'platform/components';
import {HStack, Show, Text} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {CustomerAdvancedSearchResultResponseBody} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {TestIdProps} from 'shared/web';

import {useAddress} from '../../../hooks/useAddress';
import {getMatchTextColor} from '../utils/getMatchTextColor';
import {ResultRowShell} from './ResultRowShell';
import {RowContactAside} from './RowContactAside';
import {RowPrimaryContact} from './RowPrimaryContact';

interface NaturalPersonResultRowProps extends TestIdProps {
  customer: CustomerAdvancedSearchResultResponseBody;
  onSelect: (customer: CustomerAdvancedSearchResultResponseBody, contactId?: string) => void;
}

export function NaturalPersonResultRow(props: NaturalPersonResultRowProps) {
  const {composeAddress} = useAddress();
  const person = props.customer.foundingPerson;

  const address = composeAddress(person?.address);

  const primaryContact = props.customer.primaryContact;

  return (
    <ResultRowShell
      avatar={<Avatar icon="social/person" size="small" />}
      aside={
        <RowContactAside
          phone={person?.phone}
          phoneMatchType={person?.phoneMatchType}
          email={person?.email}
        />
      }
      onSelect={() => props.onSelect(props.customer)}
      data-testid={props['data-testid']}
    >
      <HStack spacing={2} align="center">
        <Text size="small" color={getMatchTextColor(person?.firstNameMatchType)} alternative>
          {person?.firstName}
        </Text>
        <Show when={isNotNilOrEmpty(person?.lastName)}>
          <Text size="small" color={getMatchTextColor(person?.lastNameMatchType)} alternative>
            {person?.lastName}
          </Text>
        </Show>
        <Flag
          label={i18n.t('entity.person.labels.physicalPerson')}
          colorScheme="teal"
          size="small"
        />
      </HStack>
      <Show when={isNotNilOrEmpty(address)}>
        <Text size="xSmall" color="tertiary" noWrap>
          {address}
        </Text>
      </Show>

      <Show when={primaryContact && primaryContact.id !== person?.id}>
        <RowPrimaryContact contact={primaryContact} />
      </Show>
    </ResultRowShell>
  );
}
